import {Avatar, Box, Card, CardHeader, CardContent, Divider, Grid, Stack, Typography, useTheme} from '@mui/material';
import {BookOutlined, Clear} from '@mui/icons-material';
// import BookIcon from 'assets/images/icons/book.svg';
import {useSelector} from 'react-redux';
import {selectSelectedProcess} from 'app/store/appState/appStateSlice';
import {CardType, IRole, phaseColorsConstants} from 'app/types/Shared';
import {ArtifactCards} from 'components/ArtifactCards';
import {selectAllArtifacts} from 'app/store/artifacts/artifactSlice';
import {EmptyState} from 'components/EmptyState/EmptyState';

type abbrevProps = {
  emptyState?: boolean;
};

export default function Abbreviation({emptyState = false}: abbrevProps) {
  const theme = useTheme();
  const process = useSelector(selectSelectedProcess); // current process selected
  const roleCategories: Array<IRole> = [];
  const allArtifacts = useSelector(selectAllArtifacts);
  const abbrevKey = allArtifacts.filter((x) => x.key === 'ELVT-ABBREV-KEY')?.at(0);
  process?.roles.map((r) => {
    if (!roleCategories.find((x) => x.role_category.label === r.role.role_category.label)) {
      roleCategories.push(r.role);
    }
  });
  const phases = process?.phases.map((p) => p.phase.name);

  const phaseKey = (phase: string) => {
    const findColor = phaseColorsConstants.find((phaseItem) => phaseItem.name == phase);
    return (
      <Box
        bgcolor={findColor?.color}
        borderRadius={1}
        width={40}
        height={28}
        sx={{display: 'flex', alignItems: 'center', alignContent: 'center', justifyContent: 'center'}}
      >
        <Typography color='white'>
          <b>{findColor?.abbr}</b>
        </Typography>
      </Box>
    );
  };

  const RACIList: {
    title: string;
    color: string;
    description: string;
  }[] = [
    {
      title: 'R',
      color: '#ABB436',
      description: 'Responsible for performing the task',
    },
    {
      title: 'A',
      color: '#EA5532',
      description: 'Held accountable for the success or failure of the task',
    },
    {
      title: 'C',
      color: '#DA5283',
      description: 'Consulted before the task is completed',
    },
    {
      title: 'I',
      color: '#51B1BF',
      description: 'Kept informed of the task and its status',
    },
  ];

  return (
    <>
      <Card sx={{maxWidth: 390, background: theme.palette.grey[200], px: 2}} square>
        <CardHeader avatar={<BookOutlined />} title='Abbreviation Key' sx={{p: 2}} />
        <CardContent sx={{px: 2, py: 0}}>
          {emptyState ? (
            <Box height='80vh' width='100%' py={4}>
              <EmptyState
                title='Abbreviations not defined'
                subtitle='This page does not have any abbreviations'
                icon={<Clear color='primary' fontSize='medium' />}
              />
            </Box>
          ) : (
            <>
              <Stack spacing={1} sx={{pt: 2, borderTop: `1px solid ${theme.palette.grey[600]}`}}>
                <Typography variant='h5'>Roles</Typography>
                {roleCategories?.map((role) => (
                  <Box key={role.abbrev}>
                    <Grid container spacing={2} justifyContent='center' alignItems='center'>
                      <Grid item xs={3}>
                        <Typography variant='h3'>{role.role_category.abbrev}:</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant='body1'>{role.role_category.label}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
              <Divider sx={{my: 2}} />
              <Stack spacing={2}>
                <Typography variant='h5'>RACI</Typography>
                {RACIList.map((item, idx) => (
                  <Box key={`raci-${idx}`} sx={{width: '100%'}}>
                    <Grid container spacing={0} justifyContent='center' alignItems='center'>
                      <Grid item xs={1.25}>
                        <Avatar sx={{width: 20, height: 20, bgcolor: item.color}}>
                          <Typography variant='subtitle1' color='white'>
                            <b>{item.title}</b>
                          </Typography>
                        </Avatar>
                      </Grid>
                      <Grid item xs={10.75}>
                        <Typography variant='body1'>{item.description}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
              <Divider sx={{my: 2}} />
              <Stack spacing={2}>
                <Typography variant='h5'>Study Stage</Typography>
                {phases?.map((phase) => (
                  <Box key={phase} sx={{width: 200}}>
                    <Grid container spacing={0} justifyContent='center' alignItems='center'>
                      <Grid item xs={3}>
                        <Typography variant='h3'>{phaseKey(phase)}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant='body1'>{phase}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>
              {abbrevKey && (
                <Stack spacing={2} my={2}>
                  <Typography variant='h5'>Additional Resources</Typography>
                  <ArtifactCards
                    data={{
                      ...abbrevKey,
                      card: CardType.document,
                      created_dt: abbrevKey.created_dt.substring(0, 10) ?? ' ',
                    }}
                  />
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
